import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, MessageCircle, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { tagStyle } from "@/lib/tags";
import ReactionBar from "@/components/ReactionBar";
import ShareBar from "@/components/ShareBar";
import RelatedPosts from "@/components/RelatedPosts";
import Seo from "@/components/Seo";
import { absoluteImageUrl } from "@/components/ImageUploader";
import { useAuth } from "@/context/AuthContext";
import CoffeeBackground from "@/components/CoffeeBackground";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export const PostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const notesRef = useRef(null);
  const [notesInView, setNotesInView] = useState(false);

  const scrollToNotes = () => {
    notesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (!notesRef.current) return undefined;
    const obs = new IntersectionObserver(([entry]) => setNotesInView(entry.isIntersecting), { rootMargin: "0px 0px -20% 0px" });
    obs.observe(notesRef.current);
    return () => obs.disconnect();
  }, [post]);

  // Pozele din corpul textului: text → poză dreapta → text → poză stânga → text → poză dreapta → text.
  const contentWithImages = useMemo(() => {
    if (!post) return "";
    const imgs = (post.body_images || []).filter((i) => i && i.url).slice(0, 3);
    if (!imgs.length) return post.content;
    const k = imgs.length;
    const holder = document.createElement("div");
    holder.innerHTML = post.content;

    const DBL = /(?:<br\s*\/?>\s*){2,}/i;
    const BR = /<br\s*\/?>/i;
    const isEmptyHtml = (h) => !h.replace(/<br\s*\/?>/gi, "").replace(/&nbsp;/gi, " ").trim();
    // Desfacem învelișurile de tip <span style="..."> (text lipit din alte aplicații).
    const unwrap = (h) => {
      let out = (h || "").trim();
      let m = out.match(/^<span[^>]*>([\s\S]*)<\/span>$/i);
      let guard = 0;
      while (m && guard++ < 5) {
        out = m[1].trim();
        m = out.match(/^<span[^>]*>([\s\S]*)<\/span>$/i);
      }
      return out;
    };

    // 1) Blocuri: paragrafele (P/DIV/text liber/inline) se pot sparge; titlurile, citatele, listele rămân unități.
    const FIXED = /^(H1|H2|H3|H4|H5|H6|UL|OL|BLOCKQUOTE|FIGURE|TABLE|PRE|IMG|HR)$/;
    let blocks = [];
    let buffer = "";
    const flushBuffer = () => {
      const c = unwrap(buffer);
      if (!isEmptyHtml(c)) blocks.push({ p: true, html: c });
      buffer = "";
    };
    const pushP = (h) => {
      const c = unwrap(h);
      if (!isEmptyHtml(c)) blocks.push({ p: true, html: c });
    };
    [...holder.childNodes].forEach((node) => {
      if (node.nodeType === 3) {
        buffer += node.textContent;
        return;
      }
      if (node.nodeType !== 1) return;
      if (FIXED.test(node.tagName)) {
        flushBuffer();
        blocks.push({ p: false, html: node.outerHTML });
      } else if (/^(P|DIV)$/.test(node.tagName)) {
        flushBuffer();
        if (DBL.test(node.innerHTML)) node.innerHTML.split(new RegExp(DBL.source, "gi")).forEach(pushP);
        else pushP(node.innerHTML);
      } else {
        // element inline (span, em, a…) la nivelul de sus — îl adunăm ca text de paragraf
        buffer += node.outerHTML;
      }
    });
    flushBuffer();
    if (!blocks.length) return post.content;

    const need = k + 1; // segmente de text: înainte, între și după poze
    // 2) Nu avem destule blocuri? Spargem paragrafele pe <br> simplu…
    if (blocks.length < need) {
      blocks = blocks.flatMap((b) =>
        b.p && BR.test(b.html)
          ? b.html.split(new RegExp(BR.source, "gi")).map((s) => unwrap(s)).filter((s) => !isEmptyHtml(s)).map((s) => ({ p: true, html: s }))
          : [b]
      );
    }
    // 3) …apoi, cât timp nu ajung, împărțim cel mai lung paragraf pe propoziții, în două jumătăți echilibrate.
    const SENTENCE = /(?<=[.!?…])\s+(?=[«„"A-ZĂÂÎȘȚ0-9<])/;
    const plainLen = (h) => h.replace(/<[^>]+>/g, "").length;
    let guard = 0;
    while (blocks.length < need && guard++ < 12) {
      let best = -1;
      let bestLen = 0;
      blocks.forEach((b, i) => {
        if (b.p && plainLen(b.html) > bestLen && b.html.split(SENTENCE).length >= 2) {
          best = i;
          bestLen = plainLen(b.html);
        }
      });
      if (best === -1) break;
      const sentences = blocks[best].html.split(SENTENCE).map((s) => s.trim()).filter((s) => !isEmptyHtml(s));
      const total = sentences.reduce((a, s) => a + plainLen(s), 0);
      let acc = 0;
      let cut = 1;
      for (let i = 0; i < sentences.length - 1; i += 1) {
        acc += plainLen(sentences[i]);
        if (acc >= total / 2) {
          cut = i + 1;
          break;
        }
      }
      const left = sentences.slice(0, cut).join(" ");
      const right = sentences.slice(cut).join(" ");
      const halves = [left, right].filter((s) => !isEmptyHtml(s)).map((s) => ({ p: true, html: s }));
      blocks.splice(best, 1, ...halves);
      if (halves.length < 2) break;
    }

    // 4) Distribuim pozele: mereu text înainte, între și după (dreapta, stânga, dreapta).
    const n = blocks.length;
    const sides = ["inpost-right", "inpost-left", "inpost-right"];
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
    const used = new Set();
    const figures = imgs.map((img, i) => {
      let idx = Math.round(((i + 1) * n) / (k + 1));
      idx = Math.max(1, Math.min(Math.max(n - 1, 1), idx));
      while (used.has(idx) && idx < n - 1) idx += 1;
      used.add(idx);
      return {
        idx,
        html: `<figure class="${sides[i % 3]}"><img class="inpost-img" loading="lazy" src="${esc(absoluteImageUrl(img.url))}" alt="${esc(img.alt || "")}"/></figure>`,
      };
    });
    const toHtml = (b) => (b.p ? `<p>${b.html}</p>` : b.html);
    const out = [];
    for (let b = 0; b < n; b += 1) {
      figures.filter((f) => f.idx === b).forEach((f) => out.push(f.html));
      out.push(toHtml(blocks[b]));
    }
    figures.filter((f) => f.idx >= n).forEach((f) => out.push(f.html));
    return out.join("");
  }, [post]);
  const [comments, setComments] = useState([]);
  const [author, setAuthor] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const visitedSlugRef = useRef("");

  useEffect(() => {
    if (user === null) return; // așteaptă verificarea autentificării
    if (user && user.role === "admin") return; // vizitele adminului nu se numără
    if (visitedSlugRef.current === slug) return;
    visitedSlugRef.current = slug;
    api.post(`/posts/${slug}/visit`).catch(() => {});
  }, [slug, user]);

  // Adresa din bara browserului devine linkul de share, ca orice link copiat să aibă previzualizare corectă.
  useEffect(() => {
    if (!post) return;
    const shareUrl = `/api/share/${encodeURIComponent(post.slug)}${window.location.hash}`;
    if (window.location.pathname !== `/api/share/${encodeURIComponent(post.slug)}`) {
      window.history.replaceState(window.history.state, "", shareUrl);
    }
  }, [post]);

  useEffect(() => {
    let cancelled = false;
    setPost(null);
    setNotFound(false);
    api
      .get(`/posts/${slug}`)
      .then((r) => !cancelled && setPost(r.data))
      .catch((e) => {
        if (!cancelled) {
          if (e?.response?.status === 404) setNotFound(true);
          else toast.error(formatApiError(e, "Nu am putut încărca însemnarea"));
        }
      });
    api
      .get(`/posts/${slug}/comments`)
      .then((r) => !cancelled && setComments(r.data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!author.trim() || !body.trim()) {
      toast.error("Un nume și un mesaj, te rog.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/posts/${slug}/comments`, { author_name: author.trim(), body: body.trim() });
      setBody("");
      toast.success("Mulțumesc pentru nota caldă! Apare pe blog imediat ce o aprob.");
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut trimite nota"));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteComment = async (id) => {
    try {
      await api.delete(`/admin/comments/${id}`);
      setComments((c) => c.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut șterge nota"));
    }
  };

  if (notFound) {
    return (
      <div data-testid="post-notfound" className="mx-auto max-w-2xl px-6 py-32 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Vai</p>
        <h1 className="mt-4 font-serif text-4xl text-[#F7F1E8]">Pagina asta pare să se fi rătăcit.</h1>
        <button
          onClick={() => navigate("/")}
          className="mt-8 rounded-full bg-[var(--accent)] px-6 py-3 text-xs uppercase tracking-[0.2em] text-white"
        >
          Înapoi la jurnal
        </button>
      </div>
    );
  }

  if (!post) {
    return (
      <div data-testid="post-loading" className="mx-auto max-w-2xl px-6 py-32 text-center text-sm text-[#F7F1E8]/68">
        Întorc pagina&hellip;
      </div>
    );
  }

  const category = post.category || "Însemnări";
  const categoryUrl = `/?categorie=${encodeURIComponent(category)}`;
  const postUrl = `https://la-cafea-fara-menajamente.ro/post/${post.slug}`;

  return (    <main data-testid="post-page" className="relative">
      <Seo
        title={post.title}
        path={`/post/${post.slug}`}
        description={post.excerpt || `O însemnare de ${post.author_name} pe La Cafea, Fără Menajamente.`}
        image={post.cover_image ? absoluteImageUrl(post.cover_image) : undefined}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt || undefined,
            image: post.cover_image ? absoluteImageUrl(post.cover_image) : undefined,
            datePublished: post.created_at,
            dateModified: post.updated_at,
            inLanguage: "ro",
            keywords: [post.category, ...(post.tags || [])].filter(Boolean).join(", ") || undefined,
            articleSection: post.category || undefined,
            author: { "@type": "Person", name: post.author_name, jobTitle: "Profesoară de engleză și franceză" },
            publisher: { "@type": "Person", name: "Alexia" },
            mainEntityOfPage: postUrl,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Acasă", item: "https://la-cafea-fara-menajamente.ro/" },
              { "@type": "ListItem", position: 2, name: category, item: `https://la-cafea-fara-menajamente.ro${categoryUrl}` },
              { "@type": "ListItem", position: 3, name: post.title, item: postUrl },
            ],
          },
        ]}
      />
      <CoffeeBackground slot="post" testId="post-bg" />
      <div className="relative">
        <div className="mx-auto max-w-4xl px-6 pt-10 pb-14">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            <Link
              to="/"
              data-testid="post-back-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la jurnal
            </Link>
            <nav aria-label="Traseu" data-testid="post-breadcrumbs" className="flex min-w-0 items-center gap-1.5 text-xs text-[#F7F1E8]/72">
              <Link to="/" data-testid="breadcrumb-home" className="hover:text-[#F7F1E8]">Acasă</Link>
              <ChevronRight className="h-3 w-3 shrink-0" strokeWidth={1.6} />
              <Link to={categoryUrl} data-testid="breadcrumb-category" className="truncate hover:text-[#F7F1E8]">{category}</Link>
              <ChevronRight className="h-3 w-3 shrink-0" strokeWidth={1.6} />
              <span aria-current="page" data-testid="breadcrumb-current" className="max-w-[12rem] truncate text-[#F7F1E8]/90">{post.title}</span>
            </nav>
          </div>
          <div className="mt-8">
            <p className="text-xs uppercase tracking-[0.3em] text-[#F7F1E8]/78">
              {fmt(post.created_at)} &nbsp;&middot;&nbsp; de {post.author_name}
            </p>
            <button
              type="button"
              data-testid="post-notes-count-link"
              onClick={scrollToNotes}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#2B1B13]/60 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-[#F7F1E8]/86 ring-1 ring-[color-mix(in_srgb,var(--milk)_12%,transparent)] transition-colors hover:bg-[#2B1B13] hover:text-[#F7F1E8]"
            >
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.6} />
              {comments.length === 0
                ? "Lasă-mi un gând"
                : `${comments.length} ${comments.length === 1 ? "notă" : "note"} — citește-le`}
            </button>
            <h1 data-testid="post-title" className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-[#F7F1E8] sm:text-5xl md:text-6xl">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="mt-6 max-w-2xl font-serif text-xl italic text-[#F7F1E8]/86">{post.excerpt}</p>
            )}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid="post-tags">
                {post.tags.map((t) => (
                  <span key={t} className={`rounded-full px-3 py-1 text-[11px] font-medium ${tagStyle(t)} ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]`}>
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        {post.cover_image && (
          <div className="mx-auto max-w-5xl px-6">
            <div className="overflow-hidden rounded-[var(--frame,1.5rem)]">
              <img src={post.cover_image} alt={post.cover_alt || ""} className="h-[46vh] w-full object-cover md:h-[60vh]" />
            </div>
          </div>
        )}
      </div>

      <article className="relative mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <div className="rounded-[2rem] bg-[var(--milk)] px-6 py-10 shadow-[0_24px_60px_-28px_rgba(0,0,0,0.7)] ring-1 ring-[color-mix(in_srgb,var(--accent)_35%,transparent)] sm:px-10 sm:py-12">
          <div
            data-testid="post-content"
            className="bloom-prose on-milk"
            dangerouslySetInnerHTML={{ __html: contentWithImages }}
          />
          {post.punchline && (
            <p
              data-testid="post-punchline"
              className="mt-10 border-t-2 border-[var(--accent)] pt-6 font-serif text-2xl font-medium italic leading-snug text-[var(--accent)] sm:text-3xl"
            >
              — {post.punchline}
            </p>
          )}
        </div>
        <div className="mt-6 rounded-[2rem] bg-[#2B1B13]/90 px-6 py-8 ring-1 ring-[color-mix(in_srgb,var(--milk)_14%,transparent)] backdrop-blur-[2px] sm:px-10">
          <ReactionBar slug={post.slug} initial={post.reactions} />
          <div className="mt-8">
            <ShareBar slug={post.slug} title={post.title} cover={post.cover_image} punchline={post.punchline} />
          </div>
        </div>
      </article>

      <div className="relative">
        <RelatedPosts currentSlug={post.slug} tags={post.tags} />
      </div>

      {!notesInView && (
        <button
          type="button"
          data-testid="floating-notes-btn"
          onClick={scrollToNotes}
          className="group fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 rounded-full bg-[#2B1B13]/90 px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--milk)] shadow-lg ring-1 ring-[color-mix(in_srgb,var(--milk)_12%,transparent)] backdrop-blur transition-transform hover:-translate-y-0.5 hover:shadow-xl active:scale-95"
        >
          <MessageCircle className="h-4 w-4 text-[var(--accent)]" strokeWidth={1.8} />
          Lasă-mi un gând
        </button>
      )}

      <section ref={notesRef} id="note" className="relative mx-auto max-w-2xl scroll-mt-24 px-6 pb-20">
        <div className="border-t border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pt-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Notele cititorilor</p>
          <h3 className="mt-2 font-serif text-3xl font-medium text-[#F7F1E8]">
            {comments.length === 0 ? "Spune primul ce crezi" : `${comments.length} ${comments.length === 1 ? "notă" : "note"} până acum`}
          </h3>

          <ul className="mt-8 space-y-6" data-testid="comments-list">
            {comments.map((c) => (
              <li key={c.id} className="rounded-2xl bg-[#2B1B13] p-5 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]" data-testid={`comment-${c.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-serif text-lg font-medium text-[#F7F1E8]">{c.author_name}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/58">{fmt(c.created_at)}</span>
                    {user && user.role === "admin" && (
                      <button
                        data-testid={`delete-comment-${c.id}`}
                        onClick={() => deleteComment(c.id)}
                        className="rounded-full p-1.5 text-[#F7F1E8]/58 hover:bg-red-50 hover:text-red-500"
                        aria-label="Șterge nota"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-[#F7F1E8]/90">{c.body}</p>
                {c.reply && (
                  <div
                    className="mt-4 rounded-xl border-l-4 border-[var(--accent)] bg-[color-mix(in_srgb,var(--tint-pink)_60%,transparent)] px-4 py-3"
                    data-testid={`comment-reply-${c.id}`}
                  >
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--accent-dark)]">Alexia</p>
                    <p className="mt-1.5 whitespace-pre-line text-[14px] leading-relaxed text-[#F7F1E8]/92">{c.reply}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>

          <form
            onSubmit={submitComment}
            data-testid="comment-form"
            className="mt-10 space-y-4 rounded-3xl bg-[var(--tint-butter)] p-6 sm:p-8"
          >
            <p className="font-serif text-2xl text-[#F7F1E8]">Contrazice-mă sau confirmă-mi că simți la fel</p>
            <input
              data-testid="comment-name-input"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Numele tău"
              className="w-full rounded-xl border border-transparent bg-[#2B1B13] px-4 py-3 text-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--tint-pink)]"
              maxLength={60}
            />
            <textarea
              data-testid="comment-body-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Un gând bun, o întrebare sau doar un salut…"
              rows={4}
              className="w-full rounded-xl border border-transparent bg-[#2B1B13] px-4 py-3 text-sm leading-relaxed outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--tint-pink)]"
              maxLength={2000}
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="comment-submit-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
            >
              <Send className="h-3.5 w-3.5" strokeWidth={1.6} />
              {submitting ? "Se trimite…" : "Trimite"}
            </button>
            <p className="text-[11px] text-[#F7F1E8]/62">
              Notele apar pe blog după ce le citesc și le aprob eu — durează puțin.
            </p>
          </form>
        </div>
      </section>
    </main>
  );
};

export default PostPage;
